// WebRTC DataChannel 入站边界。
// 信令层有 WebSocket 校验，但 DataChannel 是点对点的独立入口，仍然必须
// 对 JSON 大小、消息类型和频率做一次轻量检查；异常消息只能被丢弃，不能
// 进入键鼠、剪贴板、文件或视频设置业务层。

import { MAX_CLIPBOARD_LENGTH } from './datachannel';
import { MAX_CLIPBOARD_FILE_COUNT, normalizeClipboardImageMimeType } from './clipboard-file';
import { MAX_CLIPBOARD_HTML_LENGTH } from './clipboard-html';
import { MAX_CLIPBOARD_RTF_LENGTH } from './clipboard-rtf';
import { fileAckBlockIndex, MAX_FILE_NAME_LENGTH, MAX_FILE_TRANSFER_BYTES } from './filexfer';
import type { StreamFrameRate, StreamQuality } from './stream-profile';

export type DataChannelName = 'control' | 'pointer' | 'wheel' | 'clipboard' | 'cursor' | 'file';
export const DATA_CHANNEL_PROTOCOL_VERSION = 1;

const CHANNEL_MESSAGE_LIMITS: Record<DataChannelName, number> = {
  // control 仍需兼容旧版把剪贴板发在主通道的行为。
  control: 2 * 1024 * 1024,
  pointer: 8 * 1024,
  wheel: 8 * 1024,
  clipboard: 2 * 1024 * 1024,
  cursor: 512 * 1024,
  file: 64 * 1024,
};

const CHANNEL_TYPES: Record<DataChannelName, ReadonlySet<string>> = {
  control: new Set([
    'session-heartbeat', 'session-heartbeat-ack', 'input-permission', 'input-ack',
    'input-state', 'mousemove', 'mousedown', 'mouseup', 'click', 'wheel',
    'keydown', 'keyup', 'clipboard', 'clipboard-read', 'clipboard-ack',
    'video-settings', 'video-settings-ack', 'cursor-mode', 'screen',
  ]),
  pointer: new Set(['mousemove', 'mousedown', 'mouseup', 'click']),
  wheel: new Set(['wheel']),
  clipboard: new Set(['clipboard', 'clipboard-read', 'clipboard-ack']),
  cursor: new Set(['cursor', 'cursor-mode']),
  file: new Set(['file-meta', 'file-resume-request', 'file-resume', 'file-ack', 'file-end', 'file-clipboard-ack', 'file-cancel']),
};

type MessageRecord = Record<string, unknown>;

function isRecord(value: unknown): value is MessageRecord {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function utf8ByteLength(value: string): number {
  try {
    if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(value).byteLength;
  } catch {
    // 旧 WebView 没有可用 TextEncoder 时使用保守估算。
  }
  return value.length * 4;
}

function boundedString(value: unknown, max: number, required = false): boolean {
  if (typeof value !== 'string') return !required && value === undefined;
  return value.length <= max && (!required || value.length > 0);
}

function boundedId(value: unknown): boolean {
  return value === undefined || (typeof value === 'string' && /^[A-Za-z0-9._:-]{1,128}$/.test(value));
}

function requiredId(value: unknown): boolean {
  return typeof value === 'string' && /^[A-Za-z0-9._:-]{1,128}$/.test(value);
}

function finiteNumber(value: unknown, min = -Infinity, max = Infinity): boolean {
  return typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max;
}

function optionalCoordinate(value: unknown): boolean {
  return value === undefined || finiteNumber(value, 0, 1);
}

function validButton(value: unknown): boolean {
  return value === 'left' || value === 'right' || value === 'middle';
}

function validInputMessage(message: MessageRecord): boolean {
  switch (message.type) {
    case 'mousemove':
      return finiteNumber(message.x, 0, 1) && finiteNumber(message.y, 0, 1);
    case 'mousedown':
    case 'mouseup':
      return validButton(message.button) && optionalCoordinate(message.x) && optionalCoordinate(message.y) && boundedId(message.id);
    case 'click':
      return validButton(message.button) && finiteNumber(message.x, 0, 1) && finiteNumber(message.y, 0, 1) && boundedId(message.id);
    case 'wheel':
      return finiteNumber(message.deltaX, -1_000_000, 1_000_000) && finiteNumber(message.deltaY, -1_000_000, 1_000_000);
    case 'keydown':
    case 'keyup':
      return boundedString(message.key, 128, true) && boundedId(message.id) &&
        (message.repeat === undefined || typeof message.repeat === 'boolean');
    case 'input-state':
      return Array.isArray(message.keys) && message.keys.length <= 64 &&
        message.keys.every((key) => boundedString(key, 64, true)) &&
        Array.isArray(message.buttons) && message.buttons.length <= 3 && message.buttons.every(validButton);
    default:
      return false;
  }
}

function validClipboardMessage(message: MessageRecord): boolean {
  if (message.type === 'clipboard') {
    return typeof message.text === 'string' && message.text.length <= MAX_CLIPBOARD_LENGTH &&
      (message.html === undefined || boundedString(message.html, MAX_CLIPBOARD_HTML_LENGTH)) &&
      (message.rtf === undefined || boundedString(message.rtf, MAX_CLIPBOARD_RTF_LENGTH)) &&
      boundedId(message.transferId);
  }
  if (message.type === 'clipboard-read') return boundedId(message.requestId);
  if (message.type === 'clipboard-ack') {
    return requiredId(message.transferId) && typeof message.ok === 'boolean' && boundedString(message.reason, 128);
  }
  return false;
}

function validHeartbeat(message: MessageRecord): boolean {
  const timestampValid = message.ts === undefined || (Number.isSafeInteger(message.ts) && Number(message.ts) >= 0);
  const versionValid = message.protocolVersion === undefined || message.protocolVersion === DATA_CHANNEL_PROTOCOL_VERSION;
  return timestampValid && versionValid;
}

function validStreamQuality(value: unknown): value is StreamQuality {
  return value === 'auto' || value === '720p' || value === '1080p' || value === '1440p' || value === '2160p';
}

function validStreamFrameRate(value: unknown): value is StreamFrameRate {
  return value === 'auto' || value === 24 || value === 30 || value === 60;
}

function optionalInteger(value: unknown, min: number, max: number): boolean {
  return value === undefined || (Number.isSafeInteger(value) && Number(value) >= min && Number(value) <= max);
}

function validVideoSettings(message: MessageRecord): boolean {
  return (message.quality === undefined || validStreamQuality(message.quality)) &&
    (message.frameRate === undefined || validStreamFrameRate(message.frameRate));
}

function validVideoSettingsAck(message: MessageRecord): boolean {
  return typeof message.ok === 'boolean' &&
    validVideoSettings(message) &&
    (message.automatic === undefined || typeof message.automatic === 'boolean') &&
    optionalInteger(message.width, 1, 32768) &&
    optionalInteger(message.height, 1, 32768) &&
    optionalInteger(message.appliedFrameRate, 1, 240) &&
    boundedString(message.reason, 160);
}

function validCursorMessage(message: MessageRecord): boolean {
  if (message.type === 'cursor-mode') return message.mode === 'captured' || message.mode === 'overlay';
  if (message.type !== 'cursor' || typeof message.visible !== 'boolean') return false;
  if (message.data !== undefined &&
      (typeof message.data !== 'string' || message.data.length > 384 * 1024 || !message.data.startsWith('data:image/'))) return false;
  return ['width', 'height', 'hotspotX', 'hotspotY', 'sourceWidth', 'sourceHeight']
    .every((key) => message[key] === undefined || finiteNumber(message[key], 0, 8192));
}

function validFileAck(message: MessageRecord): boolean {
  const blockValid = message.blockIndex === undefined || (
    Number.isSafeInteger(message.blockIndex) &&
    Number(message.blockIndex) >= 0 &&
    Number(message.blockIndex) === fileAckBlockIndex(Number(message.offset))
  );
  return requiredId(message.id) &&
    Number.isSafeInteger(message.offset) &&
    Number(message.offset) >= 0 &&
    Number(message.offset) <= MAX_FILE_TRANSFER_BYTES &&
    blockValid &&
    (message.done === undefined || typeof message.done === 'boolean') &&
    (message.accepted === undefined || typeof message.accepted === 'boolean') &&
    boundedString(message.reason, 160);
}

function validFileResumeRequest(message: MessageRecord): boolean {
  return validFileMetaFields(message, true) &&
    (message.checkpointOffset === undefined || (
      Number.isSafeInteger(message.checkpointOffset) &&
      Number(message.checkpointOffset) >= 0 &&
      Number(message.checkpointOffset) <= Number(message.size)
    ));
}

function validFileName(value: unknown): boolean {
  return typeof value === 'string' &&
    value.length > 0 &&
    value.length <= MAX_FILE_NAME_LENGTH &&
    !/[\u0000-\u001f\u007f]/.test(value);
}

function validFilePurpose(value: unknown): boolean {
  return value === undefined || value === 'download' || value === 'clipboard';
}

/**
 * 校验 file-meta / file-resume-request 共用的元信息。业务层仍会再次校验
 * 文件名、续传偏移和剪贴板语义，但 DataChannel 边界先拒绝明显不完整或
 * 超限的结构，避免异常帧进入文件流和临时文件恢复逻辑。
 */
function validFileMetaFields(message: MessageRecord, resumeRequest = false): boolean {
  if (!requiredId(message.id) || !validFileName(message.name) ||
      !Number.isSafeInteger(message.size) || Number(message.size) < 0 ||
      Number(message.size) > MAX_FILE_TRANSFER_BYTES || !validFilePurpose(message.purpose)) {
    return false;
  }
  if (message.resume !== undefined && typeof message.resume !== 'boolean') return false;
  if (!resumeRequest && message.offset !== undefined && (
    !Number.isSafeInteger(message.offset) || Number(message.offset) < 0 ||
    Number(message.offset) > Number(message.size)
  )) return false;
  if (message.offset !== undefined && resumeRequest) return false;
  if (!validFileClipboardMeta(message)) return false;
  return true;
}

function validFileClipboardMeta(message: MessageRecord): boolean {
  const purpose = message.purpose;
  const hasClipboardFields = ['clipboardId', 'clipboardIndex', 'clipboardTotal', 'kind', 'mimeType']
    .some((key) => message[key] !== undefined);
  if (purpose !== 'clipboard') return !hasClipboardFields;
  if (!requiredId(message.clipboardId) ||
      !Number.isSafeInteger(message.clipboardIndex) ||
      !Number.isSafeInteger(message.clipboardTotal) ||
      Number(message.clipboardIndex) < 0 ||
      Number(message.clipboardTotal) <= 0 ||
      Number(message.clipboardTotal) > MAX_CLIPBOARD_FILE_COUNT ||
      Number(message.clipboardIndex) >= Number(message.clipboardTotal)) return false;
  if (message.kind !== undefined && message.kind !== 'file' && message.kind !== 'image') return false;
  if (message.kind === 'image') {
    return normalizeClipboardImageMimeType(message.mimeType) !== null;
  }
  return message.mimeType === undefined;
}

function validFileResumeResponse(message: MessageRecord): boolean {
  return requiredId(message.id) &&
    Number.isSafeInteger(message.offset) && Number(message.offset) >= 0 &&
    Number(message.offset) <= MAX_FILE_TRANSFER_BYTES &&
    typeof message.accepted === 'boolean' && boundedString(message.reason, 160);
}

function validFileEnd(message: MessageRecord): boolean {
  return requiredId(message.id) &&
    (message.sha256 === undefined ||
      (typeof message.sha256 === 'string' && /^[a-f0-9]{64}$/i.test(message.sha256)));
}

function validFileClipboardAck(message: MessageRecord): boolean {
  return requiredId(message.id) && typeof message.ok === 'boolean' && boundedString(message.reason, 160);
}

function validFileCancel(message: MessageRecord): boolean {
  return requiredId(message.id) && boundedString(message.reason, 160);
}

function validFileControlMessage(message: MessageRecord): boolean {
  switch (message.type) {
    case 'file-meta':
      return validFileMetaFields(message);
    case 'file-resume-request':
      return validFileResumeRequest(message);
    case 'file-resume':
      return validFileResumeResponse(message);
    case 'file-ack':
      return validFileAck(message);
    case 'file-end':
      return validFileEnd(message);
    case 'file-clipboard-ack':
      return validFileClipboardAck(message);
    case 'file-cancel':
      return validFileCancel(message);
    default:
      return false;
  }
}

function validControlMessage(message: MessageRecord): boolean {
  if (message.type === 'session-heartbeat' || message.type === 'session-heartbeat-ack') return validHeartbeat(message);
  if (message.type === 'clipboard' || message.type === 'clipboard-read' || message.type === 'clipboard-ack') {
    return validClipboardMessage(message);
  }
  if (['mousemove', 'mousedown', 'mouseup', 'click', 'wheel', 'keydown', 'keyup', 'input-state'].includes(String(message.type))) {
    return validInputMessage(message);
  }
  if (message.type === 'input-permission') {
    return message.status === 'authorized' || message.status === 'denied' || message.status === 'restricted' ||
      message.status === 'not-determined' || message.status === 'unsupported';
  }
  if (message.type === 'input-ack') {
    return requiredId(message.id) &&
      ['mousedown', 'mouseup', 'click', 'keydown', 'keyup'].includes(String(message.inputType)) &&
      typeof message.ok === 'boolean';
  }
  if (message.type === 'cursor-mode') return message.mode === 'captured' || message.mode === 'overlay';
  if (message.type === 'screen') return finiteNumber(message.width, 1, 32768) && finiteNumber(message.height, 1, 32768);
  if (message.type === 'video-settings') return validVideoSettings(message);
  if (message.type === 'video-settings-ack') return validVideoSettingsAck(message);
  return false;
}

function validMessage(message: MessageRecord, channel: DataChannelName): boolean {
  if (channel === 'control') return validControlMessage(message);
  if (channel === 'pointer') return validInputMessage(message);
  if (channel === 'wheel') return validInputMessage(message);
  if (channel === 'clipboard') return validClipboardMessage(message);
  if (channel === 'cursor') return validCursorMessage(message);
  if (channel === 'file') return validFileControlMessage(message);
  return false;
}

export class DataChannelRateLimiter {
  private readonly hits = new Map<string, { count: number; startedAt: number }>();

  constructor(private readonly maxPerSecond: number) {}

  allow(type: string, now = Date.now()): boolean {
    const previous = this.hits.get(type);
    if (!previous || now - previous.startedAt >= 1000) {
      this.hits.set(type, { count: 1, startedAt: now });
      return true;
    }
    if (previous.count >= this.maxPerSecond) return false;
    previous.count += 1;
    return true;
  }

  reset(): void {
    this.hits.clear();
  }
}

function rateLimitFor(channel: DataChannelName): number {
  if (channel === 'pointer') return 240;
  if (channel === 'wheel') return 180;
  if (channel === 'cursor') return 90;
  if (channel === 'file') return 240;
  if (channel === 'clipboard') return 24;
  return 360;
}

export class DataChannelMessageGuard {
  private readonly limiter: DataChannelRateLimiter;

  constructor(private readonly channel: DataChannelName) {
    this.limiter = new DataChannelRateLimiter(rateLimitFor(channel));
  }

  parse(raw: unknown): MessageRecord | null {
    if (typeof raw !== 'string' || utf8ByteLength(raw) > CHANNEL_MESSAGE_LIMITS[this.channel]) return null;
    let message: unknown;
    try {
      message = JSON.parse(raw);
    } catch {
      return null;
    }
    if (!isRecord(message) || typeof message.type !== 'string' || !CHANNEL_TYPES[this.channel].has(message.type)) return null;
    if (!this.limiter.allow(message.type)) return null;
    return validMessage(message, this.channel) ? message : null;
  }

  reset(): void {
    this.limiter.reset();
  }
}

export function dataChannelMessageLimit(channel: DataChannelName): number {
  return CHANNEL_MESSAGE_LIMITS[channel];
}
